'use client'

import { useEffect } from 'react'

import { type Editor } from '@tiptap/react'
import { BubbleMenu as TipTapBubbleMenu } from '@tiptap/react/menus'

import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { TooltipProvider } from '@/components/ui/tooltip'

import { useMediaQuery } from '@/hooks/use-media-query'

import { LinkToolbar } from '@/lib/tiptap/toolbars/link'

import { BoldToolbar } from '../toolbars/bold'
import { ItalicToolbar } from '../toolbars/italic'
import {
  hasEditorExtension,
  ToolbarProvider
} from '../toolbars/toolbar-provider'
import { UnderlineToolbar } from '../toolbars/underline'

const resolveEditorElement = (
  element: Editor['options']['element'] | undefined
): HTMLElement | null => {
  if (!element) {
    return null
  }

  if (element instanceof HTMLElement) {
    return element
  }

  if (
    typeof element === 'object' &&
    'mount' in element &&
    element.mount instanceof HTMLElement
  ) {
    return element.mount
  }

  return null
}

export function BubbleMenu({ editor }: { editor: Editor | null }) {
  const isMobile = useMediaQuery('(max-width: 640px)')

  // Prevent default context menu on mobile
  useEffect(() => {
    if (!isMobile) return

    const editorElement = resolveEditorElement(editor?.options.element)
    if (!editorElement) return

    const handleContextMenu = (e: Event) => {
      e.preventDefault()
    }

    editorElement.addEventListener('contextmenu', handleContextMenu)

    return () =>
      editorElement.removeEventListener('contextmenu', handleContextMenu)
  }, [editor, isMobile])

  if (!editor) return null

  const hasBold = hasEditorExtension(editor, 'bold')
  const hasItalic = hasEditorExtension(editor, 'italic')
  const hasUnderline = hasEditorExtension(editor, 'underline')
  const haslink = hasEditorExtension(editor, 'link')

  const hasPrimaryFormattingGroup = hasBold || hasItalic || hasUnderline

  return (
    <TooltipProvider>
      <TipTapBubbleMenu
        className="bg-background rounded-sm border shadow-sm"
        editor={editor}
        options={{
          offset: { crossAxis: 10, mainAxis: 0 },
          placement: 'bottom'
        }}
        shouldShow={({ from, to }) => {
          return editor.isEditable && editor.isFocused && from !== to
        }}
      >
        <ToolbarProvider editor={editor}>
          <ScrollArea className="h-fit w-full py-0.5">
            <div className="flex items-center gap-0.5 px-2">
              {/* Primary formatting */}
              {hasPrimaryFormattingGroup && (
                <>
                  {hasBold && <BoldToolbar />}
                  {hasItalic && <ItalicToolbar />}
                  {hasUnderline && <UnderlineToolbar />}
                  {haslink && <LinkToolbar />}
                </>
              )}
            </div>
            <ScrollBar className="h-0.5" orientation="horizontal" />
          </ScrollArea>
        </ToolbarProvider>
      </TipTapBubbleMenu>
    </TooltipProvider>
  )
}
